/**
 * "I'm in the gym, I'm not on a plan, what should I do today?"
 *
 * The plan generator answers a different question — what should the next eight
 * weeks look like — and answering it costs a conversation about goals,
 * equipment and days per week. That is the right price for a programme and far
 * too high for a Tuesday. Most sessions logged in this app have never belonged
 * to a plan, and until now the app had nothing to say about any of them.
 *
 * So: read the last fortnight, propose one workout, and let it be steered. The
 * steer box is the feature, not a refinement of it. "Cardio dominant" is a
 * different session from "45 minutes, nothing overhead" is different again from
 * silence — and silence is a real answer, meaning "you decide", which is when
 * the history becomes the whole brief.
 *
 * The suggestion is not saved anywhere until it is accepted. Nothing you didn't
 * take should turn up in your plan list, on your calendar, or as an invented
 * exercise in your library — see the note on `create=False` in the endpoint.
 * Accepting writes a one-off for today, so from that point on it is an ordinary
 * prescription and everything plan days already do applies to it.
 */

import { useState } from 'react'

import { api, ApiError } from '../api/client'
import { refreshPlans } from '../sync/engine'
import type { Exercise, PlanItem } from '../db'
import { formatTarget } from '../lib/planTarget'
import { unitFor, useDistanceOverrides, type DistanceOverrides }
  from '../lib/distance'
import type { DistancePref, Unit } from '../lib/units'

export type SuggestedItem = {
  exercise_id: number
  exercise_name: string
  category: string
  tracking: string
  target_sets: number
  target_reps: number | null
  target_duration_seconds: number | null
  target_distance_m: number | null
  target_rpe: number | null
  rest_seconds: number
  notes: string | null
}

export type Suggestion = {
  name: string
  focus: string
  rationale: string
  estimated_minutes: number
  items: SuggestedItem[]
}

type Reply = {
  suggestion: Suggestion
  based_on: {
    window_days: number
    sessions: number
    working_sets: number
    days_since_last_session: number | null
  }
  unresolved: { exercise_name: string; reason: string }[]
}

/**
 * One-tap steers, for the ones people actually want.
 *
 * They fill the box rather than sending immediately, and that is deliberate:
 * "cardio dominant" is usually the start of a sentence, not the end of one.
 * Filling it in leaves the cursor somewhere useful — ", nothing that hurts my
 * knee" — where a chip that fired the request would have spent the call.
 */
const STEERS = [
  ['Cardio', 'cardio dominant'],
  ['Upper body', 'upper body'],
  ['Legs', 'legs'],
  ['Short', 'short — I have about 30 minutes'],
  ['Easy', 'something easy, I am beaten up'],
  ["What I'm behind on", "whatever I have been neglecting lately"],
] as const

export function SuggestWorkout({
  exercises,
  unit,
  distancePref,
  date,
  onStart,
}: {
  exercises: Exercise[]
  unit: Unit
  distancePref: DistancePref
  /** The day the workout goes on. Today, in every case that reaches here. */
  date: string
  /** Accepted: a one-off now exists and is cached. Start the session. */
  onStart: (opts: { planId: number; planDayId: number; title: string }) => void
}) {
  const [open, setOpen] = useState(false)
  const [steer, setSteer] = useState('')
  const [busy, setBusy] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reply, setReply] = useState<Reply | null>(null)

  const overrides = useDistanceOverrides()
  const byId = new Map(exercises.map((e) => [e.id, e]))

  const ask = async () => {
    setBusy(true)
    setError(null)
    try {
      setReply(await api<Reply>('/ai/suggest', { body: { steer: steer.trim() } }))
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Couldn't reach the server — suggestions need a connection.",
      )
    } finally {
      setBusy(false)
    }
  }

  const accept = async () => {
    if (!reply) return
    setStarting(true)
    setError(null)
    try {
      const res = await api<{ plan: { id: number; days: { id: number }[] } }>(
        '/schedule/oneoff',
        {
          body: {
            date,
            title: reply.suggestion.name,
            items: reply.suggestion.items.map((i) => ({
              exercise_id: i.exercise_id,
              target_sets: i.target_sets,
              target_reps: i.target_reps,
              target_duration_seconds: i.target_duration_seconds,
              target_distance_m: i.target_distance_m,
              target_rpe: i.target_rpe,
              rest_seconds: i.rest_seconds,
              notes: i.notes,
            })),
          },
        },
      )
      // The one-off is a plan server-side, so the local mirror needs it before
      // the session can read its prescription — the same step the calendar's
      // one-off planner takes, and for the same reason.
      await refreshPlans()
      const day = res.plan.days[0]
      if (!day) throw new Error('the saved workout came back with no day')
      onStart({ planId: res.plan.id, planDayId: day.id, title: reply.suggestion.name })
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Couldn't save it — needs a connection.",
      )
      setStarting(false)
    }
  }

  if (!open) {
    return (
      <button
        className="secondary"
        style={{ width: '100%', marginTop: '1rem' }}
        onClick={() => setOpen(true)}
      >
        Suggest a workout
      </button>
    )
  }

  return (
    <div
      style={{
        marginTop: '1rem',
        padding: '.6rem .7rem',
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius)',
      }}
    >
      <strong style={{ fontSize: '.9rem' }}>Suggest a workout</strong>
      <p className="muted" style={{ fontSize: '.8rem', margin: '.2rem 0 .5rem' }}>
        Reads your last two weeks and proposes one session for today. Say what
        you want, or leave it blank and let it choose.
      </p>

      <label htmlFor="steer">What are you after?</label>
      <textarea
        id="steer"
        rows={2}
        value={steer}
        onChange={(e) => setSteer(e.target.value)}
        placeholder="Cardio dominant. Nothing overhead — shoulder is cranky."
        style={{
          width: '100%', padding: '.65rem .7rem', font: 'inherit',
          borderRadius: 'var(--radius)', border: '1px solid var(--border)',
          background: 'var(--surface)', color: 'var(--text)', resize: 'vertical',
        }}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem', marginTop: '.4rem' }}>
        {STEERS.map(([label, text]) => (
          <button
            key={label}
            type="button"
            className="secondary"
            style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0, fontSize: '.8rem' }}
            onClick={() => setSteer(text)}
          >
            {label}
          </button>
        ))}
      </div>

      {busy && (
        <p className="muted" style={{ fontSize: '.82rem', margin: '.6rem 0 0' }}>
          Reading your last two weeks — this takes a few seconds.
        </p>
      )}

      {error && <div className="error" style={{ marginTop: '.5rem' }}>{error}</div>}

      {reply && !busy && (
        <SuggestionCard
          reply={reply}
          byId={byId}
          unit={unit}
          distancePref={distancePref}
          overrides={overrides}
        />
      )}

      <div style={{ display: 'flex', gap: '.5rem', marginTop: '.6rem' }}>
        <button
          className="secondary"
          style={{ flex: 1 }}
          disabled={busy || starting}
          onClick={() => {
            setOpen(false)
            setReply(null)
            setError(null)
          }}
        >
          Cancel
        </button>
        <button
          className="secondary"
          style={{ flex: 1 }}
          disabled={busy || starting}
          onClick={() => void ask()}
        >
          {busy ? 'Thinking…' : reply ? 'Again' : 'Suggest'}
        </button>
        {reply && (
          <button
            style={{ flex: 2 }}
            disabled={busy || starting || reply.suggestion.items.length === 0}
            onClick={() => void accept()}
          >
            {starting ? 'Starting…' : 'Start this'}
          </button>
        )}
      </div>
    </div>
  )
}

function SuggestionCard({
  reply,
  byId,
  unit,
  distancePref,
  overrides,
}: {
  reply: Reply
  byId: Map<number, Exercise>
  unit: Unit
  distancePref: DistancePref
  overrides: DistanceOverrides
}) {
  const { suggestion, based_on: basis } = reply

  return (
    <div
      className="card"
      style={{ padding: '.6rem .7rem', margin: '.6rem 0 0' }}
    >
      <div className="row" style={{ marginBottom: '.1rem' }}>
        <strong style={{ fontSize: '.95rem' }}>{suggestion.name}</strong>
        <span className="muted" style={{ fontSize: '.8rem' }}>
          ~{suggestion.estimated_minutes} min
        </span>
      </div>

      {/* Why this, in its own words and its own numbers. Without it the card is
          an oracle: five exercises from nowhere, which is exactly as persuasive
          as five exercises picked at random. */}
      <p style={{ fontSize: '.85rem', margin: '.3rem 0 .5rem' }}>
        {suggestion.rationale}
      </p>

      <ul className="plain" style={{ margin: 0 }}>
        {suggestion.items.map((item, index) => {
          const exercise = byId.get(item.exercise_id)
          // formatTarget owns the prescription notation and reads a PlanItem.
          // Adapting to it beats a second copy of "a plank is seconds, not
          // reps" — that rule has already been got wrong once here.
          const asPlanItem = {
            target_sets: item.target_sets,
            target_reps_low: item.target_reps ?? 0,
            target_reps_high: item.target_reps ?? 0,
            target_duration_seconds: item.target_duration_seconds,
            target_distance_m: item.target_distance_m,
            target_rpe: item.target_rpe,
          } as PlanItem
          return (
            <li key={`${item.exercise_id}-${index}`} style={{ display: 'block' }}>
              <div className="row">
                <span>{item.exercise_name}</span>
                <span className="muted" style={{ fontSize: '.82rem' }}>
                  {formatTarget(
                    asPlanItem,
                    unitFor(exercise, { unit, pref: distancePref, overrides }),
                  )}
                </span>
              </div>
              {item.notes && (
                <div className="muted" style={{ fontSize: '.78rem', marginTop: '.1rem' }}>
                  {item.notes}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {/* Named, not hidden. A suggestion quietly one exercise shorter than the
          one Claude wrote is a suggestion you can't reason about — and the two
          reasons want different things from you, so they're said separately. */}
      {reply.unresolved.length > 0 && (
        <p className="muted" style={{ fontSize: '.78rem', margin: '.5rem 0 0' }}>
          {['not_in_library', 'no_target'].map((reason) => {
            const names = reply.unresolved
              .filter((u) => u.reason === reason)
              .map((u) => u.exercise_name)
            if (names.length === 0) return null
            return (
              <span key={reason} style={{ display: 'block' }}>
                Left out — {reason === 'not_in_library'
                  ? `not in your library: ${names.join(', ')}`
                  : `came back without a target: ${names.join(', ')}`}.
              </span>
            )
          })}
        </p>
      )}

      <p className="muted" style={{ fontSize: '.78rem', margin: '.5rem 0 0' }}>
        {basis.sessions === 0
          ? `Nothing logged in the last ${basis.window_days} days, so this one starts easy.`
          : `Based on ${basis.sessions} session${basis.sessions === 1 ? '' : 's'} and `
            + `${basis.working_sets} working sets in the last ${basis.window_days} days.`}
      </p>
    </div>
  )
}
